import {useEffect, useState} from 'react';
import classnames from 'classnames';
import {IoIosArrowDropleft, IoIosArrowDropright} from 'react-icons/io';
import {NavLink as Link, useLocation} from 'react-router-dom';
import {GoGlobe} from 'react-icons/go';
import {FaArchive, FaDatabase, FaTachometerAlt} from 'react-icons/fa';
import {IoCompassSharp, IoGitMerge} from 'react-icons/io5';
import {MdAssignment} from 'react-icons/md';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavItem,
  NavLink,
  UncontrolledDropdown,
} from 'reactstrap';
import {debounce} from 'lodash';

import {
  APPLICATION_GRAPH_PATH,
  DASHBOARD_PATH,
  CHANGE_REQUEST_PATH,
  DEPENDENCY_PATH,
  REPORTS_ITEM_PATH,
  RELEASE_ASSISTANT_PATH,
  REPORTS_RELATIONSHIPS_PATH, SCAN_DATABASE_PATH, SCAN_ARTIFACTS_PATH,
} from 'src/routes';
import {useStoredValue} from 'src/hooks';
import {CODELOGIC_UI_PATH} from 'src/config';
import {getActuatorInfo} from 'src/api/actuator';
import {ReactComponent as RocketIcon} from 'src/images/rocket.svg';

import './styles.css';
import {WorkspaceSelector} from '../Workspaces/Selector';

// relates to ./styles.css#240-264
const ANIMATION_DURATION = 500;
const AUTO_COLLAPSE_THRESHOLD = 1200;

const LOGO_PATH = CODELOGIC_UI_PATH + '/codelogic-logo.svg';


export const SideNav = () => {
  const [isExpanded, setIsExpanded] = useStoredValue('sidebar-expanded', window.innerWidth > AUTO_COLLAPSE_THRESHOLD);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [version, setVersion] = useState('');
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const expandEvent = new Event('expand-nav');
  const contractEvent = new Event('contract-nav');

  useEffect(() => {
    const loadVersion = async () => {
      const {data} = await getActuatorInfo();
      if (data && data.app.version) {
        setVersion(data.app.version.replaceAll('\'', ''));
      }
    };
    loadVersion();
  }, []);


  useEffect(() => {
    const handleResize = debounce(() => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 100);

    handleResize();
    window.addEventListener('resize', handleResize, false);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const doneResizing = () => {
      if (dimensions.width < AUTO_COLLAPSE_THRESHOLD) {
        if (isExpanded) {
          collapse();
        }
      } else if (dimensions.width > AUTO_COLLAPSE_THRESHOLD) {
        if (!isExpanded) {
          expand();
        }
      } else {
        return;
      }
    };
    doneResizing();
  }, [dimensions.width]);


  const {pathname} = useLocation();

  const expand = () => {
    dispatchEvent(expandEvent);
    setIsExpanding(true);
    setIsExpanded(true);
    setTimeout(() => {
      setIsExpanding(false);
    }, ANIMATION_DURATION);
  };

  const collapse = () => {
    setIsCollapsing(true);
    dispatchEvent(contractEvent);
    setTimeout(() => {
      setIsCollapsing(false);
      setIsExpanded(false);
    }, ANIMATION_DURATION);
  };

  const clickAction = isExpanding || isCollapsing ? undefined : isExpanded ? collapse : expand;

  return (
    <Navbar
      className={classnames({
        'SideNav': true,
        'SideNav--collapse-transition': isCollapsing,
        'SideNav--expand-transition': isExpanding,
        'SideNav--collapsing': isCollapsing,
        'SideNav--expanding': isExpanding,
        'SideNav--expanded': isExpanded,
        'SideNav--collapsed': !isExpanded,
      })}
      light
    >
      <Nav navbar>
        <NavItem>
          <button className="link-button nav-link" onClick={clickAction}>
            {
              isExpanded && !isExpanding ?
                <IoIosArrowDropleft title="Collapse" className="SideNav__icon" /> :
                <IoIosArrowDropright title="Expand" className="SideNav__icon" />}
          </button>
        </NavItem>
        <div className="WorkspaceCont" >
          <WorkspaceSelector isExpanded={isExpanded || false}></WorkspaceSelector>
          <NavItem>
            <NavLink tag={Link} to={APPLICATION_GRAPH_PATH}>
              <GoGlobe title="Overview" className="SideNav__icon" />
              {isExpanded && <div className="SideNav__text-container"><span>Overview</span></div>}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink tag={Link} to={DASHBOARD_PATH}>
              <FaTachometerAlt title="Dashboard" className="SideNav__icon" />
              {isExpanded && <div className="SideNav__text-container"><span>Dashboard</span></div>}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink tag={Link} to={DEPENDENCY_PATH}>
              <IoCompassSharp className="SideNav__icon" />
              {isExpanded && <div title="Explorer" className="SideNav__text-container"><span>Explorer</span></div>}
            </NavLink>
          </NavItem>
          <UncontrolledDropdown nav direction='end'>
            <DropdownToggle nav className={pathname.includes('reports') ? 'active' : ''}>
              <MdAssignment title="Reports" className="SideNav__icon" />
              {isExpanded && <div className="SideNav__text-container"><span>Reports</span></div>}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                tag={Link}
                to={REPORTS_RELATIONSHIPS_PATH}
              >
                <div className="SideNav__text-container">
                Relationship Report
                </div>
              </DropdownItem>
              <DropdownItem
                tag={Link}
                to={REPORTS_ITEM_PATH}
              >
                <div className="SideNav__text-container">
                Item Report
                </div>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <NavItem>
          <NavLink tag={Link} to={CHANGE_REQUEST_PATH}>
            <IoGitMerge title="Change Requests" className="SideNav__icon" />
            {isExpanded && <div className="SideNav__text-container"><span>Change Requests</span></div>}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink tag={Link} to={RELEASE_ASSISTANT_PATH}>
            <RocketIcon title="Release Assistant" className="SideNav__icon" />
            {isExpanded && <div className="SideNav__text-container"><span>Release Assistant</span></div>}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink tag={Link} to={SCAN_ARTIFACTS_PATH}>
            <FaArchive title="Scan Archives" className="SideNav__icon" />
            {isExpanded && <div className="SideNav__text-container"><span>Scan Archives</span></div>}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink tag={Link} to={SCAN_DATABASE_PATH}>
            <FaDatabase title="Scan Databases" className="SideNav__icon" />
            {isExpanded && <div className="SideNav__text-container"><span>Scan Databases</span></div>}
          </NavLink>
        </NavItem>
      </Nav>
      <div className='VersionInfo' >
        <div className='VersionLogo'>
          <img
            src={LOGO_PATH}
            alt="CodeLogic logo"
          />
        </div>
        <p className={classnames({
          'VersionText': true,
          'VersionText--collapse-transition': isCollapsing,
          'VersionText--expand-transition': isExpanding,
          'VersionText--collapsed': !isExpanded,
        })}>v{version}</p>
      </div>
    </Navbar>
  );
};
